import { getImageSrc } from "@web-monorepo/static-assets";
import { clsx } from "clsx";
import { useEffect, useCallback } from "react";
import AnimateUntilEnd from "components/AnimateUntilEnd";
import FullBleedScreen from "components/BeyondToolkit/FullBleedScreen";
import ToolkitAudioActivityChrome from "components/ToolkitAudioActivityChrome";
import { useAudioPlayer, playStates } from "hooks/useAudioPlayer";
import { openPointsSheet } from "src/pods/webviewInit";
import { logParentToolkitEvent } from "src/utils/logEvent";
import { BRUSH_TEETH } from "utils/beyondToolkitTools";
import styles from "./index.module.css";
import { useBrushTeeth as useBrushTeethVm } from "./useBrushTeeth.vm";

type BrushTeethProps = {
  di?: { useVm?: typeof useBrushTeethVm };
};

export default function BrushTeeth({ di }: BrushTeethProps) {
  const useVm = di?.useVm ?? useBrushTeethVm;
  const { recordUsage } = useVm();

  const brushTeeth = BRUSH_TEETH();

  useEffect(() => {
    logParentToolkitEvent({ eventName: `${brushTeeth.loggingId}.loaded` });
  }, [brushTeeth.loggingId]);

  const {
    play: playMain,
    pause,
    playState,
    secondsRemaining,
  } = useAudioPlayer({
    src: brushTeeth.mainAudio,
    expectedDuration: brushTeeth.duration,
  });

  const { play: playIntro, pause: pauseIntro } = useAudioPlayer({
    src: "/static/media/activity-brushteeth_start.mp3",
    expectedDuration: 17,
  });

  const { play: playOutro } = useAudioPlayer({
    src: "/static/media/activity-brushteeth_end.mp3",
    expectedDuration: 3,
  });

  // play intro audio clip on mount
  useEffect(() => {
    playIntro();
  }, [playIntro]);

  // pause intro clip when we start the main clip
  const play = useCallback(() => {
    pauseIntro();
    playMain();

    // send usage on first play event, and not on subsequent play events (i.e. after pause)
    recordUsage();
  }, [pauseIntro, playMain, recordUsage]);

  // play outro after the main clip
  useEffect(() => {
    if (playState === playStates.DONE) {
      playOutro();
      logParentToolkitEvent({ eventName: `${brushTeeth.loggingId}.activity_completed` });
    }
  }, [brushTeeth.loggingId, playOutro, playState]);

  return (
    <FullBleedScreen variant="brushTeeth">
      <div className={styles.screen}>
        <ToolkitAudioActivityChrome
          loggingId={brushTeeth.loggingId}
          play={play}
          pause={pause}
          playState={playState}
          secondsRemaining={secondsRemaining}
          closeAndGivePoints={() => {
            openPointsSheet();
            window.history.back();
          }}
          mainContent={
            <AnimateUntilEnd
              shouldRun={playState === playStates.PLAYING}
              element={
                <div className={styles.brushAnchor}>
                  <AnimateUntilEnd
                    shouldRun={playState === playStates.PLAYING}
                    element={<img alt="" width="192px" src={brushTeeth.imgUrl} className={styles.brushBob} />}
                  />
                </div>
              }
            />
          }
        />
        <BubbleRiseAnimation bubbleType="single" variant={1} shouldRun={playState === playStates.PLAYING} />
        <BubbleRiseAnimation bubbleType="compound" variant={2} shouldRun={playState === playStates.PLAYING} />
        <BubbleRiseAnimation bubbleType="single" variant={3} shouldRun={playState === playStates.PLAYING} />
        <BubbleRiseAnimation bubbleType="compound" variant={4} shouldRun={playState === playStates.PLAYING} />
      </div>
    </FullBleedScreen>
  );
}

// Designer-tuned position + timing for each of the 4 bubbles live in
// `index.module.css` as `.bubble1` … `.bubble4` modifiers.
type BubbleVariant = 1 | 2 | 3 | 4;

const BUBBLE_VARIANT_CLASS: Record<BubbleVariant, string> = {
  1: styles.bubble1,
  2: styles.bubble2,
  3: styles.bubble3,
  4: styles.bubble4,
};

type BubbleRiseAnimationProps = {
  bubbleType: "single" | "compound";
  variant: BubbleVariant;
  shouldRun: boolean;
};

function BubbleRiseAnimation({ bubbleType, variant, shouldRun }: BubbleRiseAnimationProps) {
  return (
    <AnimateUntilEnd
      shouldRun={shouldRun}
      element={
        <div className={clsx(styles.bubble, BUBBLE_VARIANT_CLASS[variant])}>
          <AnimateUntilEnd
            shouldRun={shouldRun}
            element={
              // `--bubble-duration` + `--bubble-delay` cascade from the parent `.bubbleN`
              <div className={styles.bubbleScale}>
                <img
                  alt=""
                  src={getImageSrc(
                    bubbleType === "compound"
                      ? "toolkit/public/static/images/compound_bubble.svg"
                      : "toolkit/public/static/images/single_bubble.svg",
                  )}
                  className={clsx(
                    styles.bubbleImage,
                    bubbleType === "compound" ? styles.bubbleImageCompound : styles.bubbleImageSingle,
                  )}
                />
              </div>
            }
          />
        </div>
      }
    />
  );
}
